import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import parse from 'html-react-parser';
import {Alert, Button, Pagination, Skeleton} from 'antd';
import 'antd/dist/reset.css';
import {FullBook} from '../../../../shared/types/api';
import Constants from '../../../../const/Constants';
import {useAppSelector} from '../../../../store/hooks';
import {premiumStatusSelector} from '../../../../store/global';
import IconCrownWhite from '../../../elements/svg_icons/IconCrownWhite';
import {openPremiumPage} from '../../../../common/utils/promocode';
import {useNavigate, useParams} from 'react-router-dom';
import * as API_BOOKS from "../../../../shared/http/books";
import {useWindowSize} from "../../../../hooks/useWindowSize";
import {useDebounce} from "../../../../hooks/useDebuonce";
import LoadingBookSkeleton from "./ui/LoadingBookSkeleton";

interface BookContentViewProps {
    book?: FullBook;
    fontSize: number;
    readChars: number;
    setReadChars: (r: number) => void;
    setBook: (b: FullBook) => void;
}

export type BookReaderRef = {
    openPageByOffset: (offset: number) => void;
};

export const BookContentView = forwardRef(
    function BookContentView(
        {book, fontSize, readChars, setReadChars, setBook}: BookContentViewProps,
        ref: React.Ref<BookReaderRef>
    ) {

        const [loading, setLoading] = useState(false);
        const containerRef = useRef<HTMLDivElement>(null);
        const paginationRef = useRef<HTMLDivElement>(null);
        const [allPages, setAllPages] = useState<React.ReactNode[]>([]);
        const [pageCharCounts, setPageCharCounts] = useState<number[]>([]);
        const [currentPage, setCurrentPage] = useState(1);
        const {width, height} = useWindowSize();

        const debouncedFontSize = useDebounce(fontSize, 700);
        const debouncedWidth = useDebounce(width, 700);
        const debouncedHeight = useDebounce(height, 700);

        const isPremium = useAppSelector(premiumStatusSelector);
        const navigate = useNavigate();

        const {id} = useParams()

        useEffect(() => {
            setLoading(true);

            requestAnimationFrame(() => {
                const height = calculatePageHeight();
                const width = containerRef.current?.clientWidth || 0;

                API_BOOKS
                    .getBook(Number(id), width, height, fontSize, 1.5)
                    .then((resp) => {
                        setBook(resp);
                        const pages = resp.pages;
                        // @ts-ignore
                        setAllPages(pages.map((p) => parse(p)));
                        setPageCharCounts(resp.charCounts);
                        setCurrentPage(1);
                        setReadChars(0);
                    })
                    .finally(() => setLoading(false));
            });
        }, [debouncedFontSize, debouncedWidth, debouncedHeight]);

        const calculatePageHeight = () => {
            if (!containerRef.current || !paginationRef.current) return 600;
            const containerHeight = containerRef.current.clientHeight;
            const paginationHeight = paginationRef.current.clientHeight;
            const style = getComputedStyle(containerRef.current);
            const paddingTop = parseFloat(style.paddingTop || '0');
            const paddingBottom = parseFloat(style.paddingBottom || '0');
            const borderTop = parseFloat(style.borderTopWidth || '0');
            const borderBottom = parseFloat(style.borderBottomWidth || '0');

            return containerHeight - paginationHeight - paddingTop - paddingBottom - borderTop - borderBottom - 28;
        };

        useImperativeHandle(ref, () => ({
            openPageByOffset,
        }));


        const openPageByOffset = (offset: number) => {
            let cumulative = 0;

            let page = 1;
            let isFound = false;

            for (let i = 0; i < pageCharCounts.length; i++) {
                cumulative += pageCharCounts[i];
                if (offset < cumulative) {
                    page = i + 1;
                    isFound = true;
                    break;
                }
            }
            if (isFound) {
                setCurrentPage(page)
            } else {
                setCurrentPage(pageCharCounts.length)
            }
        }

        const handlePageChange = (page: number) => {
            const charsRead = pageCharCounts.slice(0, page - 1).reduce((a, b) => a + b, 0);
            setReadChars(charsRead);
            setCurrentPage(page);
        };

        const isBlocked = readChars > Constants.PREMIUM_BOOK_LIMIT && currentPage > 1 && !isPremium;

        const openPremium = () => {
            openPremiumPage(navigate);
        };

        return (
            <div style={{display: 'flex', flexDirection: 'column', height: 'calc(100vh - 116px)'}}>
                {isBlocked ? (
                    <Alert
                        message="Премиум доступ необходим"
                        description={
                            <div>
                                Вы прочитали более {Constants.PREMIUM_BOOK_LIMIT.toLocaleString()} символов. Чтобы
                                продолжить, пожалуйста, купите премиум.
                                <div style={{marginTop: 12}}>
                                    <Button color="purple" variant="solid" size="large" onClick={openPremium}
                                            icon={<IconCrownWhite/>}>
                                        Подключить премиум
                                    </Button>
                                </div>
                            </div>
                        }
                        type="warning"
                        showIcon
                    />
                ) : (
                    <div
                        ref={containerRef}
                        style={{
                            fontSize,
                            lineHeight: 1.5,
                            flex: 1,
                            overflow: 'hidden'
                        }}
                    >
                        {!loading && book ? allPages[currentPage - 1] : <LoadingBookSkeleton/>}
                    </div>
                )}

                <div ref={paginationRef} style={{marginTop: '16px', textAlign: 'center'}}>
                    <Pagination
                        current={currentPage}
                        total={allPages.length * 10}
                        pageSize={10}
                        onChange={handlePageChange}
                        showSizeChanger={false}
                    />
                </div>
            </div>
        );
    }) as React.ForwardRefExoticComponent<BookContentViewProps & React.RefAttributes<BookReaderRef>>;
